import type { ReducedTaxonomy } from '@/types/blocks';

/**
 * Returns the primary category from a list of categories based on optional logic.
 *
 * This function determines the most relevant category to use as "primary" based on
 * the following priority:
 *
 * 1. If `categoryId` is provided, it attempts to find and return that category.
 * 2. If no `categoryId` is provided, it searches for the first category in the list
 *    that has a non-zero parent, then attempts to find its corresponding parent category.
 * 3. If a valid parent is found (i.e., not "Uncategorized"), it is returned.
 * 4. If no valid parent is found, it falls back to the first category in the list.
 * 5. If the resulting category is "Uncategorized", it returns `undefined`.
 *
 * @param categories - The list of categories to search from.
 * @param categoryId - Optional specific category ID to prioritize.
 * @returns The primary category object, or `undefined` if none is valid.
 */
export const getPrimaryCategory = (
	categories: ReducedTaxonomy[],
	categoryId?: number,
) => {
	if (!categories || categories.length === 0) {
		return undefined;
	}

	const isValidCategory = (category?: ReducedTaxonomy) =>
		category && category.name !== 'Uncategorized';

	if (categoryId) {
		const category = categories.find((c) => c.id === categoryId);
		return isValidCategory(category) ? category : undefined;
	}

	// Look for the first valid parent category from any child with a non-zero parent.
	// We map child terms to their corresponding parent (by ID),
	// then return the first parent that is valid (i.e., not "Uncategorized").
	const firstParent = categories
		.filter((c) => c.parent !== 0)
		.map((child) => categories.find((c) => c.id === child.parent))
		.find(isValidCategory);

	if (firstParent) {
		return firstParent;
	}

	// Fallback to the first category if no valid category is found
	const fallbackCategory = categories[0];
	return isValidCategory(fallbackCategory) ? fallbackCategory : undefined;
};
